// The look of a button, shared by Button and ButtonLink. Ported from
// design/files_v1.2/droog-admin/bundle.css (.dr-btn*): 36/30 px high,
// radius-sm, one primary per view. The focus ring is the global
// :focus-visible rule in src/index.css (2px `focus`, offset 2).
// Class names are written out in full: Tailwind only generates what it finds.

export type ButtonVariant =
  'primary' | 'secondary' | 'quiet' | 'danger' | 'danger-quiet' | 'on-action';
export type ButtonSize = 'md' | 'sm';

export interface ButtonLook {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  className?: string;
}

// A loading Button is aria-disabled, not disabled, so it keeps focus; both
// look the same.
// no-underline: a link shaped as a button (ButtonLink) is not a text link.
const BASE =
  'no-underline inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm border font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-disabled aria-disabled:cursor-not-allowed aria-disabled:opacity-disabled';

// Each variant sets its own border colour: with a shared `border-transparent`
// in BASE, Tailwind's CSS order decides which of two colours wins, and the
// secondary outline lost.
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'border-transparent bg-action text-action-on',
  secondary: 'border-control-border bg-surface text-ink',
  quiet: 'border-transparent bg-transparent text-ink',
  danger: 'border-transparent bg-danger text-danger-on',
  'danger-quiet': 'border-transparent bg-transparent text-danger',
  // A quiet button on an action-coloured surface: the BulkBar (BHP-1216).
  'on-action': 'border-transparent bg-transparent text-action-on'
};

// Left out on a button that does nothing: a hover promises a click.
const HOVER: Record<ButtonVariant, string> = {
  primary: 'hover:bg-action-hover',
  secondary: 'hover:bg-surface-subtle',
  quiet: 'hover:bg-surface-subtle',
  // The design has no hover for danger and no token for one; dimming is
  // token-free and reads the same in light and dark.
  danger: 'hover:opacity-90',
  'danger-quiet': 'hover:bg-danger-soft',
  'on-action': 'hover:bg-action-hover'
};

const SIZE: Record<ButtonSize, string> = {
  md: 'h-9 px-[14px] text-sm',
  sm: 'h-[30px] px-[10px] text-[13px]'
};

export function buttonClassName({
  variant = 'secondary',
  size = 'md',
  block = false,
  inert = false,
  className
}: ButtonLook & { inert?: boolean }): string {
  const classes = [BASE, VARIANT[variant], SIZE[size]];
  if (!inert) classes.push(HOVER[variant]);
  if (block) classes.push('w-full');
  if (className !== undefined) classes.push(className);
  return classes.join(' ');
}

/** Icons in buttons: 16px, or 14px in a small button. */
export function iconSizeFor(size: ButtonSize = 'md'): number {
  return size === 'sm' ? 14 : 16;
}
